'use client';

import * as React from 'react';
import Link from 'next/link';

import { APP_NAME } from '@workspace/common/app';
import { routes } from '@workspace/routes';
import { Logo } from '@workspace/ui/components/logo';
import { Separator } from '@workspace/ui/components/separator';
import { ThemeSwitcher } from '@workspace/ui/components/theme-switcher';
import { MarketingTypography } from '@workspace/ui/components/typography';

import { SHOW_PRODUCT_AND_RESOURCES } from '~/lib/marketing-navigation';

/**
 * Footer Component
 * Simple structure: Logo | Links | Social | Copyright
 * Includes theme switcher (removed from navbar)
 */
export function Footer(): React.JSX.Element {
  return (
    <footer className="border-t bg-muted/30 py-12 md:py-16">
      <h2 className="sr-only">Footer</h2>
      <div className="w-full px-6 md:px-8 lg:px-12 xl:px-16 mx-auto max-w-[1320px]">
        {/* Main footer content */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-8">
          {/* Left: Logo */}
          <div className="flex-shrink-0">
            <Link href={routes.marketing.Index}>
              <Logo className="h-7 w-auto text-[20px]" />
            </Link>
          </div>

          {/* Center: Navigation links */}
          <nav
            className="flex flex-wrap items-center justify-center gap-4 md:gap-6"
            aria-label="Footer navigation"
          >
            {SHOW_PRODUCT_AND_RESOURCES && (
              <>
                <Link
                  href="/product"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Product
                </Link>
                <Separator
                  orientation="vertical"
                  className="h-4 hidden md:block"
                />
                <Link
                  href="/resources"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Resources
                </Link>
                <Separator
                  orientation="vertical"
                  className="h-4 hidden md:block"
                />
              </>
            )}
            <Link
              href={routes.marketing.Pricing}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Pricing
            </Link>
            <Separator
              orientation="vertical"
              className="h-4 hidden md:block"
            />
            <Link
              href={routes.marketing.Docs}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Docs
            </Link>
            <Separator
              orientation="vertical"
              className="h-4 hidden md:block"
            />
            <Link
              href={routes.dashboard.auth.SignIn}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign In
            </Link>
            <Separator
              orientation="vertical"
              className="h-4 hidden md:block"
            />
            <Link
              href={routes.marketing.Contact}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Contact
            </Link>
          </nav>

          {/* Right: Theme switcher */}
          <div className="flex items-center gap-4">
            <ThemeSwitcher />
          </div>
        </div>

        {/* Bottom: Copyright + Legal Links */}
        <div className="pt-6 border-t">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <MarketingTypography
              variant="caption"
              color="secondary"
            >
              © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
            </MarketingTypography>
            <div className="flex items-center gap-4">
              <Link
                href={routes.marketing.PrivacyPolicy}
                className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Privacy Policy
              </Link>
              <Separator
                orientation="vertical"
                className="h-3"
              />
              <Link
                href={routes.marketing.TermsOfUse}
                className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
